import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getFile } from '../../storageService';
import BackButton from '../BackButton/BackButton';
import './FAQPage.css';

const FAQPage = () => {
    const [faqData, setFaqData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedItems, setExpandedItems] = useState({});

    useEffect(() => {
        loadFAQData();
    }, []);

    const loadFAQData = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await getFile('resources/faq.json');

            if (data && Array.isArray(data.preguntas)) {
                setFaqData(data.preguntas);
            } else if (data && Array.isArray(data.faqs)) {
                // Fallback para formato alternativo
                setFaqData(data.faqs);
            } else {
                console.error('Formato de datos recibido:', data);
                setError('El archivo no contiene el formato esperado de preguntas');
            }
        } catch (err) {
            console.error('Error loading FAQ data:', err);
            if (err.message.includes('QUIC') || err.message.includes('network')) {
                setError('Error de conexión. Por favor, intenta recargar la página o verifica tu conexión a internet.');
            } else {
                setError('Error al cargar las preguntas frecuentes: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleItem = (index) => {
        setExpandedItems(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    return (
        <div className="faq-page">
            <Helmet>
                <title>Preguntas Frecuentes - ContactaMe | Ayuda y Soporte</title>
                <meta
                    name="description"
                    content="Encontrá respuestas a las preguntas más comunes sobre ContactaMe. Cómo registrarte, publicar servicios, contactar proveedores y más."
                />
                <meta
                    name="keywords"
                    content="preguntas frecuentes, ayuda contactame, faq, soporte, tutorial, guía, como usar contactame"
                />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://contactame.com.ar/preguntas-frecuentes" />
                <meta property="og:title" content="Preguntas Frecuentes - ContactaMe" />
                <meta property="og:description" content="Encontrá respuestas a las preguntas más comunes sobre ContactaMe." />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:url" content="https://contactame.com.ar/preguntas-frecuentes" />
                <meta name="twitter:title" content="Preguntas Frecuentes - ContactaMe" />
                <meta name="twitter:description" content="Encontrá respuestas a las preguntas más comunes sobre ContactaMe." />
                <meta name="twitter:image" content="https://contactame.com.ar/logo512.png" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://contactame.com.ar/preguntas-frecuentes" />
            </Helmet>

            <div className="faq-page-container">
                <BackButton />

                <div className="faq-page-header">
                    <h1>Preguntas Frecuentes</h1>
                    <p>Encuentra respuestas a las preguntas más comunes sobre ContactaMe</p>
                </div>

                <div className="faq-page-content">
                    {loading ? (
                        <div className="faq-loading">
                            <div className="loading-spinner"></div>
                            <p>Cargando preguntas frecuentes...</p>
                        </div>
                    ) : error ? (
                        <div className="faq-error">
                            <div className="error-icon">❌</div>
                            <h3>Error al cargar las preguntas</h3>
                            <p>{error}</p>
                            <button onClick={loadFAQData} className="faq-retry-btn">
                                🔄 Reintentar
                            </button>
                        </div>
                    ) : (
                        <div className="faq-list">
                            {faqData.map((item, index) => (
                                <div key={item.id || index} className="faq-item">
                                    <button
                                        className="faq-question"
                                        onClick={() => toggleItem(index)}
                                    >
                                        <span className="faq-question-text">{item.pregunta}</span>
                                        <span className="faq-question-icon">
                                            {expandedItems[index] ?
                                                <ChevronUp size={20} /> :
                                                <ChevronDown size={20} />
                                            }
                                        </span>
                                    </button>
                                    {expandedItems[index] && (
                                        <div className="faq-answer">
                                            <p>{item.respuesta}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FAQPage;
